import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Autocomplete, AutocompleteOption, Box, Button, Chip, CircularProgress, Divider, FormControl, FormHelperText, FormLabel,
    IconButton, Input, ListItemContent, Option, Select, Sheet, Stack, Switch,
    Tab, TabList, TabPanel, Tabs, Textarea, Tooltip, Typography,
} from '@mui/joy';
import {
    Add, CardGiftcard, ContentCopy, Delete, DragIndicator, NotificationsNone,
    PlaylistAddCheck, TaskAlt,
} from '@mui/icons-material';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor,
    useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { PageStandard } from '../utils/PageStandard';
import { FeedbackContext } from '../utils/FeedbackContext';
import {
    ExperienceTask, FlowForTrigger, GeocacheCatalogEntry, GeocacheType, NotificationEvent, NotificationTarget,
    QrCodeCatalogEntry, RewardRuleType, StickerModel, TaskRewardScope, TaskTarget, TaskType, TriggerType,
} from '../utils/Model';
import {
    createGeocache, createQrCode, createTask, deleteTask, duplicateTask, getExperience, getGeocaches,
    getQrCodes, getStickers, getTaskFlows, getTasks, reorderTasks, updateTask,
} from '../utils/ViewModel';
import { getUUID } from '../utils/Utils';

// ── Draft types ───────────────────────────────────────────────────────────────

interface TriggerDraft {
    _key: string;
    id: string | null;
    trigger_type: TriggerType;
    config: Record<string, any>;
}

interface ScoringDraft {
    individual_points: number;
    group_points: number;
    target: TaskTarget;
    time_decay_enabled: boolean;
    time_decay_config: { base_points: number; min_points: number; decay_per_minute: number } | null;
}

interface NotifDraft {
    _key: string;
    id: string | null;
    event_type: NotificationEvent;
    title: string;
    body: string;
    target: NotificationTarget;
}

interface RewardRuleDraft {
    _key: string;
    id: string | null;
    scope: TaskRewardScope;
    limit_count: number | null; // null = tutti quelli che completano
    reward_type: RewardRuleType;
    sticker_id: string;
    award_title: string;
    award_description: string;
    award_valid_days: number | null;
    is_active: boolean;
}

const newRewardRuleDraft = (): RewardRuleDraft => ({
    _key: getUUID(),
    id: null,
    scope: 'GROUP',
    limit_count: 1,
    reward_type: 'STICKER',
    sticker_id: '',
    award_title: '',
    award_description: '',
    award_valid_days: 30,
    is_active: true,
});

interface TaskDraft {
    id: string | null;
    name: string;
    description: string;
    type: TaskType;
    is_active: boolean;
    task_order: number;
    content: Record<string, any>;
    triggers: TriggerDraft[];
    scoring: ScoringDraft;
    notifications: NotifDraft[];
    rewardRules: RewardRuleDraft[];
}

const defaultScoring = (): ScoringDraft => ({
    individual_points: 10,
    group_points: 0,
    target: TaskTarget.individual,
    time_decay_enabled: false,
    time_decay_config: null,
});

const emptyDraft = (task_order: number): TaskDraft => ({
    id: null,
    name: '',
    description: '',
    type: TaskType.STEP,
    is_active: true,
    task_order,
    content: {},
    triggers: [],
    scoring: defaultScoring(),
    notifications: [],
    rewardRules: [],
});

function taskToD(t: ExperienceTask): TaskDraft {
    return {
        id: t.id,
        name: t.name,
        description: t.description,
        type: t.type,
        is_active: t.is_active,
        task_order: t.task_order,
        content: t.content || {},
        triggers: t.triggers.map(tr => ({ _key: getUUID(), id: tr.id, trigger_type: tr.trigger_type, config: tr.config })),
        scoring: t.scoring ? {
            individual_points: Number(t.scoring.individual_points),
            group_points: Number(t.scoring.group_points),
            target: t.scoring.target as TaskTarget,
            time_decay_enabled: Boolean(t.scoring.time_decay_enabled),
            time_decay_config: t.scoring.time_decay_config || null,
        } : defaultScoring(),
        notifications: t.notifications.map(n => ({
            _key: getUUID(),
            id: n.id,
            event_type: n.event_type as NotificationEvent,
            title: n.title,
            body: n.body,
            target: n.target as NotificationTarget,
        })),
        rewardRules: (t.rewardRules || []).map(r => ({
            _key: getUUID(),
            id: r.id,
            scope: r.scope,
            limit_count: r.limit_count,
            reward_type: r.reward_type,
            sticker_id: r.sticker_id || '',
            award_title: r.award_title || '',
            award_description: r.award_description || '',
            award_valid_days: r.award_valid_days,
            is_active: Boolean(r.is_active),
        })),
    };
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ExperienceTasks() {
    const { id: experienceId } = useParams<{ id: string }>();
    const { setMessage, setError } = useContext(FeedbackContext);
    const [experienceTitle, setExperienceTitle] = useState('');
    const [tasks, setTasks] = useState<ExperienceTask[]>([]);
    const [flows, setFlows] = useState<FlowForTrigger[]>([]);
    const [stickers, setStickers] = useState<StickerModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState<TaskDraft | null>(null);
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        if (!experienceId) return;
        const [t, f, s] = await Promise.all([getTasks(experienceId), getTaskFlows(experienceId), getStickers()]);
        setTasks(t);
        setFlows(f);
        setStickers(s);
    }, [experienceId]);

    useEffect(() => {
        const load = async () => {
            if (!experienceId) return;
            const [data] = await Promise.all([
                getExperience(experienceId),
                refresh(),
            ]);
            if (data) setExperienceTitle(data.experience.title);
            setLoading(false);
        };
        load();
    }, [experienceId, refresh]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = tasks.findIndex(t => t.id === active.id);
        const newIdx = tasks.findIndex(t => t.id === over.id);
        const reordered = arrayMove(tasks, oldIdx, newIdx).map((t, i) => ({ ...t, task_order: i }));
        setTasks(reordered);
        if (experienceId) {
            await reorderTasks(experienceId, reordered.map(t => ({ id: t.id, task_order: t.task_order })));
        }
    };

    const handleSelectTask = (task: ExperienceTask) => setDraft(taskToD(task));

    const handleNewTask = () => setDraft(emptyDraft(tasks.length));

    const handleSave = async () => {
        if (!draft || !experienceId) return;
        if (!draft.name.trim()) { setError('Il nome del task è obbligatorio'); return; }
        setSaving(true);
        const payload = {
            name: draft.name.trim(),
            description: draft.description,
            type: draft.type,
            is_active: draft.is_active,
            task_order: draft.task_order,
            content: draft.content,
            triggers: draft.triggers.map(({ trigger_type, config }) => ({ trigger_type, config })),
            scoring: draft.scoring,
            notifications: draft.notifications.map(({ event_type, title, body, target }) => ({ event_type, title, body, target })),
            rewardRules: draft.rewardRules.map(({ scope, limit_count, reward_type, sticker_id, award_title, award_description, award_valid_days, is_active }) => ({
                scope,
                limit_count,
                reward_type,
                sticker_id: reward_type === 'STICKER' ? sticker_id : null,
                award_title: reward_type === 'AWARD' ? award_title.trim() : null,
                award_description: reward_type === 'AWARD' ? award_description.trim() : null,
                award_valid_days: reward_type === 'AWARD' ? award_valid_days : null,
                is_active,
            })),
        };
        let ok: boolean;
        if (draft.id) {
            ok = await updateTask(draft.id, payload as any);
        } else {
            const newId = await createTask(experienceId, payload as any);
            ok = !!newId;
        }
        setSaving(false);
        if (ok) {
            setMessage('Task salvato');
            await refresh();
            setDraft(null);
        }
    };

    const handleDelete = async (taskId: string) => {
        if (!window.confirm('Eliminare questo task?')) return;
        const ok = await deleteTask(taskId);
        if (ok) {
            setMessage('Task eliminato');
            if (draft?.id === taskId) setDraft(null);
            await refresh();
        }
    };

    const handleDuplicate = async (taskId: string) => {
        const newId = await duplicateTask(taskId);
        if (newId) { setMessage('Task duplicato'); await refresh(); }
    };

    if (loading) {
        return (
            <PageStandard title="Task avanzati" sidebarVisible={true}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageStandard>
        );
    }

    return (
        <PageStandard
            title={`Task — ${experienceTitle || experienceId}`}
            sidebarVisible={true}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 0,
                    height: 'calc(100vh - 120px)',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'lg',
                }}
            >
                {/* ── Left: task list ─────────────────────────────────────── */}
                <Sheet
                    sx={{
                        width: 320,
                        minWidth: 320,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Button
                            fullWidth
                            size="sm"
                            startDecorator={<Add />}
                            onClick={handleNewTask}
                        >
                            Nuovo task
                        </Button>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                        {tasks.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <PlaylistAddCheck sx={{ fontSize: 40, color: 'text.tertiary', mb: 1 }} />
                                <Typography level="body-sm" color="neutral">
                                    Nessun task ancora. Creane uno!
                                </Typography>
                            </Box>
                        ) : (
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                                    <Stack spacing={0.5}>
                                        {tasks.map(task => (
                                            <SortableTaskItem
                                                key={task.id}
                                                task={task}
                                                selected={draft?.id === task.id}
                                                onClick={() => handleSelectTask(task)}
                                                onDuplicate={() => handleDuplicate(task.id)}
                                                onDelete={() => handleDelete(task.id)}
                                            />
                                        ))}
                                    </Stack>
                                </SortableContext>
                            </DndContext>
                        )}
                    </Box>
                </Sheet>

                {/* ── Right: editor ───────────────────────────────────────── */}
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {draft ? (
                        <TaskEditorPanel
                            draft={draft}
                            flows={flows}
                            stickers={stickers}
                            saving={saving}
                            onChange={setDraft}
                            onSave={handleSave}
                            onCancel={() => setDraft(null)}
                        />
                    ) : (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5, color: 'text.tertiary' }}>
                            <TaskAlt sx={{ fontSize: 52 }} />
                            <Typography level="title-md" color="neutral">
                                Seleziona un task da modificare o crea un nuovo task
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </PageStandard>
    );
}

// ── SortableTaskItem ──────────────────────────────────────────────────────────

function typeLabel(type: TaskType): string {
    return { QUESTION: 'Domanda', QR_CODE_SCAN: 'QR Code', STEP: 'Step', GEOCACHE: 'Geocache' }[type] ?? type;
}

function typeColor(type: TaskType): 'primary' | 'warning' | 'neutral' | 'success' {
    return { QUESTION: 'primary', QR_CODE_SCAN: 'warning', STEP: 'neutral', GEOCACHE: 'success' }[type] as any ?? 'neutral';
}

interface SortableTaskItemProps {
    task: ExperienceTask;
    selected: boolean;
    onClick: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
}

function SortableTaskItem({ task, selected, onClick, onDuplicate, onDelete }: SortableTaskItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: task.id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Sheet
            ref={setNodeRef}
            style={style}
            variant={selected ? 'soft' : 'plain'}
            color={selected ? 'primary' : 'neutral'}
            sx={{
                borderRadius: 'sm',
                p: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': { bgcolor: selected ? undefined : 'background.level1' },
            }}
            onClick={onClick}
        >
            <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: 'text.tertiary', display: 'flex' }}>
                <DragIndicator fontSize="small" />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                    <Chip size="sm" color={typeColor(task.type)} variant="soft">{typeLabel(task.type)}</Chip>
                    {!task.is_active && <Chip size="sm" color="neutral" variant="outlined">Off</Chip>}
                </Stack>
                <Typography
                    level="body-sm"
                    fontWeight={selected ? 'bold' : 'normal'}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {task.name || <em style={{ color: 'var(--joy-palette-text-tertiary)' }}>Senza nome</em>}
                </Typography>
                <Typography level="body-xs" color="neutral">
                    {task.triggers.length} trigger{task.triggers.length !== 1 ? 's' : ''} · {task.notifications.length} notifiche
                </Typography>
            </Box>

            <Stack direction="row" spacing={0.25} onClick={e => e.stopPropagation()}>
                <Tooltip title="Duplica">
                    <IconButton size="sm" variant="plain" onClick={onDuplicate}><ContentCopy sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Elimina">
                    <IconButton size="sm" variant="plain" color="danger" onClick={onDelete}><Delete sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
            </Stack>
        </Sheet>
    );
}

// ── TaskEditorPanel ───────────────────────────────────────────────────────────

interface EditorProps {
    draft: TaskDraft;
    flows: FlowForTrigger[];
    stickers: StickerModel[];
    saving: boolean;
    onChange: (d: TaskDraft) => void;
    onSave: () => void;
    onCancel: () => void;
}

function TaskEditorPanel({ draft, flows, stickers, saving, onChange, onSave, onCancel }: EditorProps) {
    const set = <K extends keyof TaskDraft>(key: K, val: TaskDraft[K]) =>
        onChange({ ...draft, [key]: val });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography level="title-md" sx={{ flex: 1 }}>
                    {draft.id ? 'Modifica task' : 'Nuovo task'}
                </Typography>
                <Button variant="plain" color="neutral" size="sm" onClick={onCancel}>Annulla</Button>
                <Button size="sm" onClick={onSave} loading={saving}>Salva</Button>
            </Box>

            {/* Tabs */}
            <Tabs defaultValue={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <TabList>
                    <Tab>Dettagli</Tab>
                    <Tab>
                        Trigger
                        {draft.triggers.length > 0 && (
                            <Chip size="sm" sx={{ ml: 0.5 }}>{draft.triggers.length}</Chip>
                        )}
                    </Tab>
                    <Tab>Punteggi</Tab>
                    <Tab>
                        Notifiche
                        {draft.notifications.length > 0 && (
                            <Chip size="sm" sx={{ ml: 0.5 }}>{draft.notifications.length}</Chip>
                        )}
                    </Tab>
                    {draft.type === TaskType.GEOCACHE && (
                        <Tab>
                            Premio
                            {draft.rewardRules.length > 0 && (
                                <Chip size="sm" sx={{ ml: 0.5 }}>{draft.rewardRules.length}</Chip>
                            )}
                        </Tab>
                    )}
                </TabList>

                {/* Tab 1 – Dettagli */}
                <TabPanel value={0} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                    <Stack spacing={2.5}>
                        <FormControl required>
                            <FormLabel>Nome</FormLabel>
                            <Input
                                value={draft.name}
                                onChange={e => set('name', e.target.value)}
                                placeholder="Nome del task"
                            />
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Tipo</FormLabel>
                                <Select
                                    value={draft.type}
                                    onChange={(_, v) => v && set('type', v as TaskType)}
                                >
                                    <Option value={TaskType.STEP}>Step informativo</Option>
                                    <Option value={TaskType.QUESTION}>Domanda a scelta multipla</Option>
                                    <Option value={TaskType.QR_CODE_SCAN}>QR Code</Option>
                                    <Option value={TaskType.GEOCACHE}>Geocache</Option>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ pt: 3 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Switch
                                        checked={draft.is_active}
                                        onChange={e => set('is_active', e.target.checked)}
                                    />
                                    <Typography level="body-sm">Attivo</Typography>
                                </Stack>
                            </FormControl>
                        </Stack>

                        <FormControl>
                            <FormLabel>Descrizione</FormLabel>
                            <Textarea
                                value={draft.description}
                                onChange={e => set('description', e.target.value)}
                                minRows={3}
                                placeholder="Descrizione del task (visibile ai partecipanti)"
                            />
                        </FormControl>

                        {/* Type-specific content */}
                        {draft.type === TaskType.QUESTION && (
                            <QuestionContentEditor
                                content={draft.content}
                                onChange={c => set('content', c)}
                            />
                        )}
                        {draft.type === TaskType.QR_CODE_SCAN && (
                            <QrContentEditor
                                content={draft.content}
                                onChange={c => set('content', c)}
                            />
                        )}
                        {draft.type === TaskType.GEOCACHE && (
                            <GeocacheContentEditor
                                content={draft.content}
                                onChange={c => set('content', c)}
                            />
                        )}
                    </Stack>
                </TabPanel>

                {/* Tab 2 – Trigger */}
                <TabPanel value={1} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                    <TriggersEditor
                        triggers={draft.triggers}
                        flows={flows}
                        onChange={t => set('triggers', t)}
                    />
                </TabPanel>

                {/* Tab 3 – Punteggi */}
                <TabPanel value={2} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                    <ScoringEditor
                        scoring={draft.scoring}
                        onChange={s => set('scoring', s)}
                    />
                </TabPanel>

                {/* Tab 4 – Notifiche */}
                <TabPanel value={3} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                    <NotificationsEditor
                        notifications={draft.notifications}
                        onChange={n => set('notifications', n)}
                    />
                </TabPanel>

                {/* Tab 5 – Premio (solo Geocache) */}
                {draft.type === TaskType.GEOCACHE && (
                    <TabPanel value={4} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                        <RewardRulesEditor
                            rules={draft.rewardRules}
                            stickers={stickers}
                            onChange={r => set('rewardRules', r)}
                        />
                    </TabPanel>
                )}
            </Tabs>
        </Box>
    );
}

// ── QuestionContentEditor ─────────────────────────────────────────────────────
// answers[0] è la risposta corretta; le altre sono distrattori

function QuestionContentEditor({ content, onChange }: { content: Record<string, any>; onChange: (c: Record<string, any>) => void }) {
    const answers: string[] = content.answers && content.answers.length > 0 ? content.answers : ['', ''];
    const set = (key: string, val: any) => onChange({ ...content, [key]: val });

    const setAnswer = (i: number, val: string) => {
        const next = [...answers];
        next[i] = val;
        set('answers', next);
    };
    const addAnswer = () => set('answers', [...answers, '']);
    const removeAnswer = (i: number) => {
        if (answers.length <= 2) return; // minimo 2 risposte
        set('answers', answers.filter((_, idx) => idx !== i));
    };

    return (
        <Box>
            <Divider sx={{ mb: 2 }}>Configurazione domanda</Divider>
            <Stack spacing={2}>
                <FormControl required>
                    <FormLabel>Domanda</FormLabel>
                    <Textarea
                        value={content.question || ''}
                        onChange={e => set('question', e.target.value)}
                        minRows={2}
                        placeholder="La domanda da porre ai partecipanti"
                    />
                </FormControl>

                <Box>
                    <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>Risposte</Typography>
                    <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                        La prima risposta è quella corretta. Le altre sono i distrattori. Nell'app verranno mostrate in ordine casuale.
                    </Typography>
                    <Stack spacing={1}>
                        {answers.map((a, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                <Chip
                                    size="sm"
                                    color={i === 0 ? 'success' : 'neutral'}
                                    variant={i === 0 ? 'solid' : 'outlined'}
                                    sx={{ minWidth: 64, justifyContent: 'center' }}
                                >
                                    {i === 0 ? 'Corretta' : `Errata ${i}`}
                                </Chip>
                                <Input
                                    value={a}
                                    onChange={e => setAnswer(i, e.target.value)}
                                    placeholder={i === 0 ? 'Risposta corretta…' : `Risposta errata ${i}…`}
                                    sx={{ flex: 1 }}
                                    color={i === 0 ? 'success' : 'neutral'}
                                />
                                <IconButton
                                    size="sm"
                                    color="danger"
                                    variant="plain"
                                    disabled={answers.length <= 2}
                                    onClick={() => removeAnswer(i)}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}
                        <Button size="sm" variant="outlined" startDecorator={<Add />} onClick={addAnswer} sx={{ alignSelf: 'flex-start' }}>
                            Aggiungi risposta errata
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

// ── QrContentEditor ───────────────────────────────────────────────────────────

// Picks a fixed QR from the reusable catalog (same "create now, bind
// location later" flow as the geocache catalog) instead of typing the raw
// QR string by hand.

function QrContentEditor({ content, onChange }: { content: Record<string, any>; onChange: (c: Record<string, any>) => void }) {
    const [options, setOptions] = useState<QrCodeCatalogEntry[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getQrCodes().then(setOptions).finally(() => setLoading(false));
    }, []);

    const handleCreateNew = async () => {
        const created = await createQrCode({});
        if (created) {
            setOptions(prev => [created, ...prev]);
            onChange({ ...content, qr_code_id: created.id });
        }
    };

    const selected = options.find(o => o.id === content.qr_code_id);

    return (
        <Box>
            <Divider sx={{ mb: 2 }}>Configurazione QR Code</Divider>
            <FormControl required>
                <FormLabel>QR fisso</FormLabel>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Select
                        placeholder={loading ? 'Caricamento…' : 'Seleziona un QR fisso'}
                        value={content.qr_code_id || null}
                        onChange={(_, v) => v && onChange({ ...content, qr_code_id: v as string })}
                        sx={{ minWidth: 260 }}
                    >
                        {options.map(o => (
                            <Option key={o.id} value={o.id}>
                                {(o.label || o.code)}{o.lat == null ? ' · senza posizione' : ''}
                            </Option>
                        ))}
                    </Select>
                    <Button size="sm" variant="soft" onClick={handleCreateNew}>
                        + Nuovo QR (senza posizione)
                    </Button>
                </Stack>
            </FormControl>
            {!content.qr_code_id && content.qr_content && (
                <Typography level="body-sm" color="warning" sx={{ mt: 1 }}>
                    Task legacy con testo libero "{content.qr_content}" — seleziona un QR fisso per aggiornarlo.
                </Typography>
            )}
            {selected && (
                <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                    {selected.lat != null
                        ? `Coordinate: ${selected.lat}, ${selected.lng}`
                        : "Nessuna posizione ancora: legala in un secondo momento dall'app (Profilo → Dev Tools)."}
                </Typography>
            )}
        </Box>
    );
}

// ── GeocacheContentEditor ─────────────────────────────────────────────────────
// Picks a fixed geocache from the reusable catalog (same "create now, bind
// location later" flow as the QR catalog). Every geocache is found either by
// scanning a QR or tapping an NFC tag placed at the spot — `type` says
// which, `code` is the value expected from that scan/tap. Proximity (for the
// compass/distance hint in-game) is evaluated through a GEO_PROXIMITY
// trigger on the Trigger tab — copy the coordinates shown here into it (or
// leave one for the app's Dev Tools to bind a location to later).

const GEOCACHE_TYPE_LABELS: Record<GeocacheType, string> = {
    QR_CODE: 'QR Code',
    NFC: 'NFC',
};

function GeocacheContentEditor({ content, onChange }: { content: Record<string, any>; onChange: (c: Record<string, any>) => void }) {
    const [options, setOptions] = useState<GeocacheCatalogEntry[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getGeocaches().then(setOptions).finally(() => setLoading(false));
    }, []);

    const handleCreateNew = async () => {
        const label = window.prompt('Nome del geocache (es. "Fontana piazza X")');
        if (!label) return;
        const type: GeocacheType = window.confirm('Trovato tramite NFC? (Annulla = QR Code)') ? 'NFC' : 'QR_CODE';
        const created = await createGeocache({ label, type });
        if (created) {
            setOptions(prev => [created, ...prev]);
            onChange({ ...content, geocache_id: created.id });
        }
    };

    const selected = options.find(o => o.id === content.geocache_id);

    return (
        <Box>
            <Divider sx={{ mb: 2 }}>Configurazione Geocache</Divider>
            <FormControl required>
                <FormLabel>Geocache fisso</FormLabel>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Select
                        placeholder={loading ? 'Caricamento…' : 'Seleziona un geocache'}
                        value={content.geocache_id || null}
                        onChange={(_, v) => v && onChange({ ...content, geocache_id: v as string })}
                        sx={{ minWidth: 260 }}
                    >
                        {options.map(o => (
                            <Option key={o.id} value={o.id}>
                                {o.label} · {GEOCACHE_TYPE_LABELS[o.type]}{o.lat == null ? ' · senza posizione' : ''}
                            </Option>
                        ))}
                    </Select>
                    <Button size="sm" variant="soft" onClick={handleCreateNew}>
                        + Nuovo geocache (senza posizione)
                    </Button>
                </Stack>
            </FormControl>
            {selected && (
                <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                    Tipo: {GEOCACHE_TYPE_LABELS[selected.type]} · Codice: {selected.code}
                    {selected.lat != null
                        ? ` · Coordinate: ${selected.lat}, ${selected.lng} — impostale nel trigger "Vicinanza a coordinate" nella tab Trigger se vuoi anche l'hint di prossimità.`
                        : " · Nessuna posizione ancora: legala in un secondo momento dall'app (Profilo → Dev Tools)."}
                </Typography>
            )}
        </Box>
    );
}

// ── TriggersEditor ────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<TriggerType, string> = {
    [TriggerType.STEP_ACTIVATED]: 'Step raggiunto (attivato)',
    [TriggerType.STEP_COMPLETE]: 'Step completato',
    [TriggerType.ANY_STEP_COMPLETE]: 'Uno qualsiasi di N step',
    [TriggerType.GEO_ZONE]: 'Ingresso zona geografica',
    [TriggerType.GEO_PROXIMITY]: 'Vicinanza a coordinate',
    [TriggerType.AFTER_STEP_DELAY]: 'Dopo X minuti da uno step',
    [TriggerType.TIME_WINDOW]: 'Finestra temporale',
    [TriggerType.MANUAL]: 'Manuale (admin)',
};

interface TriggersEditorProps {
    triggers: TriggerDraft[];
    flows: FlowForTrigger[];
    onChange: (t: TriggerDraft[]) => void;
}

function TriggersEditor({ triggers, flows, onChange }: TriggersEditorProps) {
    const allSteps = flows.flatMap(f => f.steps.map(s => ({ ...s, flowTitle: f.title })));
    const addTrigger = () => onChange([
        ...triggers,
        { _key: getUUID(), id: null, trigger_type: TriggerType.STEP_ACTIVATED, config: {} },
    ]);
    const remove = (key: string) => onChange(triggers.filter(t => t._key !== key));
    const update = (key: string, patch: Partial<TriggerDraft>) =>
        onChange(triggers.map(t => t._key === key ? { ...t, ...patch } : t));

    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
                    Un task si attiva quando si verifica almeno uno dei trigger configurati. Se non hai trigger, il task non si attiverà mai automaticamente.
                </Typography>
            </Box>

            {triggers.map(trigger => (
                <Sheet key={trigger._key} variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
                    <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Tipo trigger</FormLabel>
                                <Select
                                    value={trigger.trigger_type}
                                    onChange={(_, v) => v && update(trigger._key, { trigger_type: v as TriggerType, config: {} })}
                                >
                                    {Object.values(TriggerType).map(tt => (
                                        <Option key={tt} value={tt}>{TRIGGER_LABELS[tt]}</Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <Tooltip title="Rimuovi trigger">
                                <IconButton
                                    size="sm" color="danger" variant="plain"
                                    sx={{ alignSelf: 'flex-end', mb: 0.5 }}
                                    onClick={() => remove(trigger._key)}
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <TriggerConfigEditor
                            trigger={trigger}
                            allSteps={allSteps}
                            onChange={config => update(trigger._key, { config })}
                        />
                    </Stack>
                </Sheet>
            ))}

            <Button variant="outlined" startDecorator={<Add />} onClick={addTrigger} sx={{ alignSelf: 'flex-start' }}>
                Aggiungi trigger
            </Button>
        </Stack>
    );
}

// ── TriggerConfigEditor ───────────────────────────────────────────────────────

interface AllStep {
    id: string;
    flow_id: string;
    flowTitle: string;
    type: string;
    display_title: string;
    step_order: number;
}

function TriggerConfigEditor({
    trigger,
    allSteps,
    onChange,
}: {
    trigger: TriggerDraft;
    allSteps: AllStep[];
    onChange: (c: Record<string, any>) => void;
}) {
    const cfg = trigger.config;
    const set = (k: string, v: any) => onChange({ ...cfg, [k]: v });

    const selectedStepIds: string[] = cfg.step_ids || [];

    if (trigger.trigger_type === TriggerType.STEP_ACTIVATED || trigger.trigger_type === TriggerType.STEP_COMPLETE || trigger.trigger_type === TriggerType.ANY_STEP_COMPLETE) {
        const label = trigger.trigger_type === TriggerType.STEP_ACTIVATED
            ? 'Step che attiva il task (basta raggiungerlo)'
            : trigger.trigger_type === TriggerType.STEP_COMPLETE
                ? 'Step da completare'
                : 'Almeno uno di questi step';
        return (
            <StepMultiPicker
                label={label}
                allSteps={allSteps}
                selectedIds={selectedStepIds}
                onChange={ids => set('step_ids', ids)}
            />
        );
    }

    if (trigger.trigger_type === TriggerType.GEO_ZONE || trigger.trigger_type === TriggerType.GEO_PROXIMITY) {
        return (
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Latitudine</FormLabel>
                        <Input type="number" value={cfg.lat ?? ''} onChange={e => set('lat', parseFloat(e.target.value))} placeholder="45.0706" />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Longitudine</FormLabel>
                        <Input type="number" value={cfg.lng ?? ''} onChange={e => set('lng', parseFloat(e.target.value))} placeholder="7.6868" />
                    </FormControl>
                </Stack>
                <FormControl>
                    <FormLabel>Raggio (metri)</FormLabel>
                    <Input type="number" value={cfg.radius_meters ?? ''} onChange={e => set('radius_meters', parseInt(e.target.value))} placeholder="100" />
                </FormControl>
            </Stack>
        );
    }

    if (trigger.trigger_type === TriggerType.AFTER_STEP_DELAY) {
        return (
            <Stack spacing={1.5}>
                <StepMultiPicker
                    label="Step di riferimento"
                    allSteps={allSteps}
                    selectedIds={selectedStepIds}
                    onChange={ids => set('step_ids', ids)}
                />
                <FormControl>
                    <FormLabel>Ritardo (secondi)</FormLabel>
                    <Input type="number" value={cfg.delay_seconds ?? ''} onChange={e => set('delay_seconds', parseInt(e.target.value))} placeholder="300" />
                </FormControl>
            </Stack>
        );
    }

    if (trigger.trigger_type === TriggerType.TIME_WINDOW) {
        return (
            <Stack direction="row" spacing={1}>
                <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Inizio</FormLabel>
                    <Input type="datetime-local" value={cfg.start ?? ''} onChange={e => set('start', e.target.value)} />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Fine</FormLabel>
                    <Input type="datetime-local" value={cfg.end ?? ''} onChange={e => set('end', e.target.value)} />
                </FormControl>
            </Stack>
        );
    }

    if (trigger.trigger_type === TriggerType.MANUAL) {
        return <Typography level="body-sm" color="neutral">Attivazione manuale dall'admin. Nessuna configurazione aggiuntiva.</Typography>;
    }

    return null;
}

// ── StepMultiPicker ───────────────────────────────────────────────────────────
// Searchable, flow-grouped step selector — used by trigger config to attach
// steps without a flat list forcing a scroll through everything.

function StepMultiPicker({ label, allSteps, selectedIds, onChange }: {
    label: string;
    allSteps: AllStep[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}) {
    const stepLabel = (s: AllStep) => s.display_title || `Step ${s.step_order + 1}`;
    const selected = allSteps.filter(s => selectedIds.includes(s.id));

    return (
        <FormControl>
            <FormLabel sx={{ mb: 1 }}>{label}</FormLabel>
            {allSteps.length === 0 ? (
                <Typography level="body-sm" color="neutral">Nessuno step disponibile. Crea prima un flow.</Typography>
            ) : (
                <Autocomplete
                    multiple
                    limitTags={3}
                    placeholder="Cerca uno step per nome, tipo o flow…"
                    options={allSteps}
                    value={selected}
                    groupBy={s => s.flowTitle}
                    getOptionLabel={stepLabel}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, v) => onChange(v.map(s => s.id))}
                    renderOption={(props, option) => (
                        <AutocompleteOption {...props} key={option.id}>
                            <ListItemContent>
                                <Typography level="body-sm">{stepLabel(option)}</Typography>
                                <Typography level="body-xs" color="neutral">{option.type}</Typography>
                            </ListItemContent>
                        </AutocompleteOption>
                    )}
                />
            )}
        </FormControl>
    );
}

// ── ScoringEditor ─────────────────────────────────────────────────────────────

function ScoringEditor({ scoring, onChange }: { scoring: ScoringDraft; onChange: (s: ScoringDraft) => void }) {
    const set = <K extends keyof ScoringDraft>(k: K, v: ScoringDraft[K]) => onChange({ ...scoring, [k]: v });
    const setDecay = (k: string, v: number) => onChange({
        ...scoring,
        time_decay_config: { ...(scoring.time_decay_config || { base_points: 100, min_points: 0, decay_per_minute: 5 }), [k]: v },
    });

    return (
        <Stack spacing={2.5}>
            <FormControl>
                <FormLabel>Target punteggio</FormLabel>
                <Select value={scoring.target} onChange={(_, v) => v && set('target', v as TaskTarget)}>
                    <Option value={TaskTarget.individual}>Solo individuale</Option>
                    <Option value={TaskTarget.group}>Solo gruppo</Option>
                    <Option value={TaskTarget.both}>Individuale + Gruppo</Option>
                </Select>
            </FormControl>

            {scoring.target !== TaskTarget.group && (
                <FormControl>
                    <FormLabel>Punti individuali</FormLabel>
                    <Input
                        type="number"
                        value={scoring.individual_points}
                        onChange={e => set('individual_points', parseFloat(e.target.value) || 0)}
                        endDecorator="pt"
                    />
                </FormControl>
            )}

            {scoring.target !== TaskTarget.individual && (
                <FormControl>
                    <FormLabel>Punti gruppo</FormLabel>
                    <Input
                        type="number"
                        value={scoring.group_points}
                        onChange={e => set('group_points', parseFloat(e.target.value) || 0)}
                        endDecorator="pt"
                    />
                </FormControl>
            )}

            <Divider />

            <FormControl>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                        checked={scoring.time_decay_enabled}
                        onChange={e => {
                            set('time_decay_enabled', e.target.checked);
                            if (e.target.checked && !scoring.time_decay_config) {
                                set('time_decay_config', { base_points: 100, min_points: 10, decay_per_minute: 5 });
                            }
                        }}
                    />
                    <Typography level="body-sm">Decadimento nel tempo</Typography>
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                    I punti diminuiscono man mano che passa il tempo dal momento in cui la task diventa disponibile.
                </Typography>
            </FormControl>

            {scoring.time_decay_enabled && scoring.time_decay_config && (
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Punti massimi</FormLabel>
                            <Input type="number" value={scoring.time_decay_config.base_points} onChange={e => setDecay('base_points', parseFloat(e.target.value) || 0)} endDecorator="pt" />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Punti minimi</FormLabel>
                            <Input type="number" value={scoring.time_decay_config.min_points} onChange={e => setDecay('min_points', parseFloat(e.target.value) || 0)} endDecorator="pt" />
                        </FormControl>
                    </Stack>
                    <FormControl>
                        <FormLabel>Decadimento per minuto</FormLabel>
                        <Input type="number" value={scoring.time_decay_config.decay_per_minute} onChange={e => setDecay('decay_per_minute', parseFloat(e.target.value) || 0)} endDecorator="pt/min" />
                    </FormControl>
                </Stack>
            )}
        </Stack>
    );
}

// ── NotificationsEditor ───────────────────────────────────────────────────────

const NOTIF_EVENT_LABELS: Record<NotificationEvent, string> = {
    [NotificationEvent.ON_ACTIVATE]: 'Quando il task si attiva',
    [NotificationEvent.ON_PROXIMITY]: 'Quando il partecipante è vicino',
    [NotificationEvent.ON_COMPLETE]: 'Quando il task è completato',
};

const NOTIF_TARGET_LABELS: Record<NotificationTarget, string> = {
    [NotificationTarget.all_participants]: 'Tutti i partecipanti',
    [NotificationTarget.group]: 'Il gruppo',
    [NotificationTarget.individual]: 'Il singolo partecipante',
};

function NotificationsEditor({ notifications, onChange }: { notifications: NotifDraft[]; onChange: (n: NotifDraft[]) => void }) {
    const add = () => onChange([
        ...notifications,
        { _key: getUUID(), id: null, event_type: NotificationEvent.ON_ACTIVATE, title: '', body: '', target: NotificationTarget.all_participants },
    ]);
    const remove = (key: string) => onChange(notifications.filter(n => n._key !== key));
    const update = (key: string, patch: Partial<NotifDraft>) =>
        onChange(notifications.map(n => n._key === key ? { ...n, ...patch } : n));

    return (
        <Stack spacing={2.5}>
            <Typography level="body-sm" color="neutral">
                Configura le push notification automatiche legate a questo task. Vengono inviate ai dispositivi registrati con un APN token.
            </Typography>

            {notifications.map(notif => (
                <Sheet key={notif._key} variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Quando inviare</FormLabel>
                                <Select value={notif.event_type} onChange={(_, v) => v && update(notif._key, { event_type: v as NotificationEvent })}>
                                    {Object.values(NotificationEvent).map(e => (
                                        <Option key={e} value={e}>{NOTIF_EVENT_LABELS[e]}</Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>A chi</FormLabel>
                                <Select value={notif.target} onChange={(_, v) => v && update(notif._key, { target: v as NotificationTarget })}>
                                    {Object.values(NotificationTarget).map(t => (
                                        <Option key={t} value={t}>{NOTIF_TARGET_LABELS[t]}</Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <Tooltip title="Rimuovi">
                                <IconButton size="sm" color="danger" variant="plain" sx={{ mt: 3 }} onClick={() => remove(notif._key)}>
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <FormControl>
                            <FormLabel>Titolo notifica</FormLabel>
                            <Input value={notif.title} onChange={e => update(notif._key, { title: e.target.value })} placeholder="Es: Nuova sfida disponibile!" />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Testo notifica</FormLabel>
                            <Textarea value={notif.body} onChange={e => update(notif._key, { body: e.target.value })} minRows={2} placeholder="Es: Trovate il QR nascosto in questa zona..." />
                        </FormControl>
                    </Stack>
                </Sheet>
            ))}

            <Button variant="outlined" startDecorator={<NotificationsNone />} onClick={add} sx={{ alignSelf: 'flex-start' }}>
                Aggiungi notifica
            </Button>
        </Stack>
    );
}

// ── RewardRulesEditor ───────────────────────────────────────────────────────────

function RewardRulesEditor({ rules, stickers, onChange }: {
    rules: RewardRuleDraft[]; stickers: StickerModel[]; onChange: (r: RewardRuleDraft[]) => void;
}) {
    const add = () => onChange([...rules, newRewardRuleDraft()]);
    const remove = (key: string) => onChange(rules.filter(r => r._key !== key));
    const update = (key: string, patch: Partial<RewardRuleDraft>) =>
        onChange(rules.map(r => r._key === key ? { ...r, ...patch } : r));

    return (
        <Stack spacing={2.5}>
            <Typography level="body-sm" color="neutral">
                Assegna automaticamente un premio o uno sticker a chi completa questo geocache, in base a chi arriva
                prima. Puoi aggiungere più regole insieme — es. un premio speciale al primo assoluto e uno sticker
                al primo di ogni gruppo.
            </Typography>

            {rules.map(rule => (
                <Sheet key={rule._key} variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Tipo di ricompensa</FormLabel>
                                <Select
                                    value={rule.reward_type}
                                    onChange={(_, v) => v && update(rule._key, { reward_type: v as RewardRuleType })}
                                >
                                    <Option value="STICKER">Sticker</Option>
                                    <Option value="AWARD">Premio (coupon)</Option>
                                </Select>
                            </FormControl>
                            <Tooltip title="Rimuovi">
                                <IconButton size="sm" color="danger" variant="plain" sx={{ mt: 3 }} onClick={() => remove(rule._key)}>
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>A chi</FormLabel>
                                <Select
                                    value={rule.scope}
                                    onChange={(_, v) => v && update(rule._key, { scope: v as TaskRewardScope })}
                                >
                                    <Option value="GROUP">Primo/i di ogni gruppo</Option>
                                    <Option value="INDIVIDUAL">Primo/i in assoluto (tutta l'esperienza)</Option>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Quanti</FormLabel>
                                <Select
                                    value={rule.limit_count === null ? 'ALL' : 'N'}
                                    onChange={(_, v) => update(rule._key, { limit_count: v === 'ALL' ? null : (rule.limit_count ?? 1) })}
                                >
                                    <Option value="N">I primi N</Option>
                                    <Option value="ALL">Tutti quelli che completano</Option>
                                </Select>
                            </FormControl>
                        </Stack>

                        {rule.limit_count !== null && (
                            <FormControl required>
                                <FormLabel>N</FormLabel>
                                <Input
                                    type="number"
                                    slotProps={{ input: { min: 1 } }}
                                    value={rule.limit_count}
                                    onChange={e => update(rule._key, { limit_count: Math.max(1, Number(e.target.value) || 1) })}
                                />
                                <FormHelperText>
                                    1 = solo il primo{rule.scope === 'GROUP' ? ' di ogni gruppo' : ''}, 2 = i primi due, ecc.
                                </FormHelperText>
                            </FormControl>
                        )}

                        {rule.reward_type === 'STICKER' ? (
                            <FormControl required>
                                <FormLabel>Sticker</FormLabel>
                                <Select
                                    value={rule.sticker_id || null}
                                    placeholder="Seleziona uno sticker…"
                                    onChange={(_, v) => update(rule._key, { sticker_id: (v as string) || '' })}
                                >
                                    {stickers.map(s => (
                                        <Option key={s.id} value={s.id}>{s.title}</Option>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <>
                                <FormControl required>
                                    <FormLabel>Titolo premio</FormLabel>
                                    <Input
                                        value={rule.award_title}
                                        onChange={e => update(rule._key, { award_title: e.target.value })}
                                        placeholder="es. Bevanda omaggio al bar della tappa"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Descrizione</FormLabel>
                                    <Textarea
                                        minRows={2}
                                        value={rule.award_description}
                                        onChange={e => update(rule._key, { award_description: e.target.value })}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Giorni di validità</FormLabel>
                                    <Input
                                        type="number"
                                        slotProps={{ input: { min: 1 } }}
                                        value={rule.award_valid_days ?? ''}
                                        onChange={e => update(rule._key, { award_valid_days: e.target.value ? Number(e.target.value) : null })}
                                        placeholder="Nessuna scadenza"
                                    />
                                </FormControl>
                            </>
                        )}

                        <Switch
                            checked={rule.is_active}
                            onChange={e => update(rule._key, { is_active: e.target.checked })}
                            endDecorator="Regola attiva"
                        />
                    </Stack>
                </Sheet>
            ))}

            <Button variant="outlined" startDecorator={<CardGiftcard />} onClick={add} sx={{ alignSelf: 'flex-start' }}>
                Aggiungi regola premio
            </Button>
        </Stack>
    );
}
